import { useEffect, useMemo, useState } from "react";
import {
  IdentificationCard,
  DownloadSimple,
  MapPin,
  Envelope,
  Phone,
  User,
  CheckCircle,
  XCircle,
  CaretDown,
  Funnel,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { listLeads, leadsCsvUrl } from "../lib/api";

/** Formats a value like "AU / NSW / Sydney" — falls back to "—" when empty. */
function fmtLoc(row) {
  const parts = [row.geo_country, row.geo_state, row.geo_city].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
}

function fmtValue(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function LeadRow({ row, expanded, onToggle }) {
  return (
    <>
      <tr
        className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer"
        onClick={onToggle}
        data-testid={`lead-row-${row.id}`}
      >
        <td className="p-3 font-mono text-[11px] text-zinc-500 whitespace-nowrap">
          {(row.submitted_at || "").slice(0, 16).replace("T", " ")}
        </td>
        <td className="p-3">
          <div className="text-sm text-zinc-200 flex items-center gap-1.5">
            <User size={12} className="text-zinc-500 flex-shrink-0" />
            {row.contact_name || <span className="text-zinc-600">—</span>}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono flex items-center gap-1.5 mt-1">
            <Envelope size={11} />
            {row.contact_email || "—"}
          </div>
          {row.contact_phone && (
            <div className="text-[11px] text-zinc-500 font-mono flex items-center gap-1.5 mt-1">
              <Phone size={11} />
              {row.contact_phone}
            </div>
          )}
        </td>
        <td className="p-3">
          <div className="text-sm text-zinc-200 truncate max-w-[220px]" title={row.ad_set_title}>
            {row.ad_set_title || <span className="text-zinc-600">—</span>}
          </div>
          <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mt-1">
            {row.platform} · {row.variant_id?.slice(0, 6)}
          </div>
        </td>
        <td className="p-3 text-[11px] text-zinc-400 font-mono">
          <div className="flex items-center gap-1.5">
            <MapPin size={11} className="text-zinc-500" />
            {fmtLoc(row)}
          </div>
        </td>
        <td className="p-3 text-center">
          <span
            className="inline-block px-2 py-1 font-mono text-[11px] font-bold"
            style={{
              color: row.qualified ? "#4ade80" : "#f87171",
              background: row.qualified ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)",
              border: `1px solid ${row.qualified ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
            }}
            data-testid={`lead-qual-${row.id}`}
          >
            {row.qualified ? (
              <span className="flex items-center gap-1"><CheckCircle size={11} weight="fill" /> {row.score}</span>
            ) : (
              <span className="flex items-center gap-1"><XCircle size={11} weight="fill" /> {row.score}</span>
            )}
          </span>
        </td>
        <td className="p-3 text-right">
          <CaretDown
            size={14}
            className={`text-zinc-500 transition-transform ${expanded ? "rotate-180 text-[#ccff00]" : ""}`}
          />
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#080808] border-b border-white/5" data-testid={`lead-detail-${row.id}`}>
          <td colSpan={6} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="label-mono mb-2">survey answers</div>
                <div className="space-y-2">
                  {(row.answers_readable || []).length === 0 ? (
                    <div className="text-xs text-zinc-500 font-mono">no answers recorded</div>
                  ) : (
                    (row.answers_readable || []).map((a) => (
                      <div key={a.question_id} className="border-l-2 border-[#ccff00]/30 pl-3">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                          {a.question}
                        </div>
                        <div className="text-sm text-zinc-200 mt-0.5">{fmtValue(a.value)}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div>
                <div className="label-mono mb-2">context</div>
                <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs font-mono">
                  <dt className="text-zinc-500">survey</dt>
                  <dd className="text-zinc-200 truncate">{row.survey_name || "—"}</dd>
                  <dt className="text-zinc-500">ad url</dt>
                  <dd className="text-zinc-200 truncate">
                    {row.ad_set_url ? (
                      <a href={row.ad_set_url} target="_blank" rel="noreferrer" className="text-[#ccff00] hover:underline">
                        {row.ad_set_url.slice(0, 40)}…
                      </a>
                    ) : "—"}
                  </dd>
                  <dt className="text-zinc-500">country / state / city</dt>
                  <dd className="text-zinc-200">{fmtLoc(row)}</dd>
                  <dt className="text-zinc-500">ip</dt>
                  <dd className="text-zinc-200">{row.geo_ip || "—"}</dd>
                  <dt className="text-zinc-500">score</dt>
                  <dd className="text-zinc-200">{row.score}</dd>
                  <dt className="text-zinc-500">qualified</dt>
                  <dd className={row.qualified ? "text-emerald-400" : "text-red-400"}>
                    {row.qualified ? "yes" : "no"}
                  </dd>
                </dl>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function Leads() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | qualified | unqualified
  const [expanded, setExpanded] = useState({});

  const params = useMemo(() => {
    if (filter === "qualified") return { qualified: "true" };
    if (filter === "unqualified") return { qualified: "false" };
    return {};
  }, [filter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listLeads(params)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.response?.data?.detail || "Failed to load leads");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [params]);

  const doExport = () => {
    // Streaming download — hit the endpoint with the current filter params.
    const url = leadsCsvUrl(params);
    // Uses same-origin cookie for auth; opens in a hidden anchor.
    const a = document.createElement("a");
    a.href = url;
    a.download = "lead_slips.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("CSV export started");
  };

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const qualified = data?.qualified ?? 0;

  return (
    <div className="max-w-6xl">
      <div className="mb-10 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="label-mono mb-3">inbox</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter">
            Lead <span className="font-black text-[#ccff00]">Slips</span>
          </h1>
          <p className="text-zinc-400 text-sm mt-3 max-w-xl">
            Every survey submission across all campaigns. Filter, expand a row
            to see every answer, or export the full dataset (survey answers +
            geo + contact info) as CSV.
          </p>
        </div>
        <button
          onClick={doExport}
          data-testid="export-leads-csv"
          className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-semibold px-5 py-3 flex items-center gap-2 transition-colors"
          disabled={total === 0}
        >
          <DownloadSimple size={16} weight="bold" />
          Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6" data-testid="lead-stats">
        <div className="bg-[#0a0a0a] border border-white/10 p-4">
          <div className="label-mono mb-1">total leads</div>
          <div className="font-display text-3xl font-black text-[#ccff00]" data-testid="lead-total">
            {total}
          </div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 p-4">
          <div className="label-mono mb-1">qualified</div>
          <div className="font-display text-3xl font-black text-emerald-400" data-testid="lead-qualified">
            {qualified}
          </div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 p-4">
          <div className="label-mono mb-1">qual rate</div>
          <div className="font-display text-3xl font-black text-white">
            {total > 0 ? Math.round((qualified / total) * 100) : 0}%
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Funnel size={13} className="text-zinc-500" />
        <span className="label-mono">filter</span>
        {["all", "qualified", "unqualified"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-3 py-1.5 font-mono text-xs uppercase tracking-wider border transition-colors ${
              filter === f
                ? "bg-[#ccff00]/10 border-[#ccff00]/40 text-[#ccff00]"
                : "bg-transparent border-white/10 text-zinc-500 hover:text-white hover:border-white/20"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 overflow-hidden" data-testid="leads-table">
        <table className="w-full text-left">
          <thead className="bg-[#050505] border-b border-white/10">
            <tr>
              <th className="p-3 label-mono">submitted</th>
              <th className="p-3 label-mono">contact</th>
              <th className="p-3 label-mono">ad set</th>
              <th className="p-3 label-mono">location</th>
              <th className="p-3 label-mono text-center">score</th>
              <th className="p-3 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-zinc-500 font-mono text-sm">
                  loading leads…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="p-12 text-center">
                  <IdentificationCard size={32} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
                  <div className="text-zinc-400 text-sm">
                    No lead slips yet. Attach a survey to an ad set and lead slips will show up here after
                    visitors submit.
                  </div>
                </td>
              </tr>
            )}
            {items.map((row) => (
              <LeadRow
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() =>
                  setExpanded((prev) => ({ ...prev, [row.id]: !prev[row.id] }))
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      {total > items.length && (
        <div className="text-center text-zinc-500 font-mono text-xs mt-3">
          showing {items.length} of {total} · export CSV for the full set
        </div>
      )}
    </div>
  );
}
